import axios from "axios";
import config from "./AuthUser";
import store from "../utils/appStore";
import { getAuthToken } from "../utils/authUtils";

const API_BASE_URL = config.apiBaseUrl;
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    // Don't set default Content-Type - let axios handle it automatically
    // For GET requests: axios will handle query params properly
    // For POST requests: we can set Content-Type per request
  },
});

const excludedEndpoints = [
  "/login",
  "/employeeVerification",
  "/getAllBlogs",
  "/getsettingsforweb",
  "/getDriversForWeb",
  "/getAllFaqs",
  "/getAllFeatures",
  "/getJobsForWeb",
];

apiClient.interceptors.request.use(
  (request) => {
    // Get token from multiple sources with fallback
    let token = store.getState().user?.token;

    // Fallback to centralized auth utility
    if (!token) {
      token = getAuthToken();
    }

    // Additional fallback to direct localStorage access
    if (!token) {
      try {
        const userStr = localStorage.getItem('user-info');
        if (userStr) {
          const userItem = JSON.parse(userStr);
          token = userItem?.value?.token || userItem?.token;
        }
      } catch (error) {
        console.error('Error getting token from localStorage:', error);
      }
    }

    console.log(`API Request: ${request.method.toUpperCase()} ${request.url}`, token ? 'Token present' : 'No token');

    if (
      !excludedEndpoints.some((endpoint) => request.url.includes(endpoint)) &&
      token
    ) {
      request.headers["Authorization"] = `Bearer ${token}`;
    }

    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling 401 errors
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, response.status);
    return response;
  },
  (error) => {
    console.error(`API Error: ${error.config?.method?.toUpperCase() || 'UNKNOWN'} ${error.config?.url || 'UNKNOWN'}`, error.response?.status, error.message);

    if (error.response?.status === 401) {
      // Token is invalid or expired, clear local storage and redirect to login
      localStorage.removeItem("user-info");
      // Force page reload to clear any remaining state
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/** Leave Settings --> ***/
export const leaveSettingsApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addLeads", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getAllLeaveTypes");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getUserData: async () => {
    try {
      const response = await apiClient.get("/getUserData");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  convertedbyId: async (id) => {
    try {
      const response = await apiClient.post(`/convertlead/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  viewMoreById: async (id) => {
    try {
      const response = await apiClient.post(`/getDriverDataForPortal/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
/** Leads --> leads***/
export const leadsApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addLeads", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    selection,
    searchValue,
    selectionStatus,
    startDate,
    endDate
  ) => {
    try {
      const response = await apiClient.get("/getallLeads", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          ...(selectionStatus && { status: selectionStatus }),
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });
      return { success: true, data: response?.data };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          status: error?.response?.status,
          message: error?.response?.data?.error || "An error occurred",
        };
      } else if (error.request) {
        return {
          success: false,
          status: null,
          message: "No response from the server",
        };
      } else {
        return {
          success: false,
          status: null,
          message: error?.message,
        };
      }
    }
  },
  getUserData: async () => {
    try {
      const response = await apiClient.get("/getUserData");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  convertedbyId: async (id) => {
    try {
      const response = await apiClient.post(`/convertlead/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  viewMoreById: async (id) => {
    try {
      const response = await apiClient.get(`/getleadsbyid/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  followUpbyId: async (id, formData) => {
    try {
      const response = await apiClient.post(`/assignfollowup/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  updatebyId: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateLeads/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
export const employeeHRApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addNotice", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getAll: async (params = {}) => {
    try {
      const response = await apiClient.get("/getAllEmployees", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteNotice/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  editById: async (id) => {
    try {
      const response = await apiClient.get(`/getEmployeesbyID/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  updateById: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateNotice/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
};
export const employeeShiftSettingApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addEmployeeShifts", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getAllEmployeeShifts");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteEmployeeShift/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching :", error);
      throw error;
    }
  },
  editById: async (id) => {
    try {
      const response = await apiClient.get(`/getEmployeeShiftById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching :", error);
      throw error;
    }
  },
  updateById: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateEmployeeShift/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching :", error);
      throw error;
    }
  },
};

/** Leads --> Sales***/
export const leadsMainApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getallLeads", {
        params: {
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching:", error);
      throw error;
    }
  },
  followUpbyId: async (id, formData) => {
    try {
      const response = await apiClient.post(`/assignfollowup/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  viewMoreById: async (id) => {
    try {
      const response = await apiClient.get(`/getleadsbyid/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
export const leadSalesApis = {
  addFollowUp: async (formData) => {
    try {
      const response = await apiClient.post("/sales/addFollowup", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    selection,
    searchValue,
    selectionStatus,
    startDate,
    endDate
  ) => {
    console.log(selection, searchValue, "allvalue og");
    try {
      const response = await apiClient.get("/sales/getSalesData", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          ...(selectionStatus && { status: selectionStatus }),
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  viewSalesData: async (id) => {
    try {
      const response = await apiClient.get(`/sales/viewSalesData/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Departments:", error);
      throw error;
    }
  },
  followUpHistory: async (id) => {
    try {
      const response = await apiClient.get(`/sales/getFollowupHistory/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Departments:", error);
      throw error;
    }
  },
  followUpEmployee: async (id) => {
    try {
      const response = await apiClient.get(`/sales/getFollowupus/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Departments:", error);
      throw error;
    }
  },
};
/** Driver Leave Requests ***/
export const driverLeaveApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addDriverLeaves", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding driver leave:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getalldriverleaves?limit=1000&page=1");
      return response.data;
    } catch (error) {
      console.error("Error fetching driver leaves:", error);
      throw error;
    }
  },
  leaveViewMoreId: async (id) => {
    try {
      const response = await apiClient.get(`/getDriverLeaveById/${id}`);
      console.log("API response:", response);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  remarkById: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/approveDriverLeave/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  callUsers: async (phoneNumber, callType = "m") => {
    try {
      const response = await apiClient.post("/callUsers", {
        calling_party_b: phoneNumber,
        call_type: callType
      });
      return response.data;
    } catch (error) {
      console.error("Error initiating call:", error);
      throw error;
    }
  }
};
export const driverSalesApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/AddCorpclient", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/sales/getSalesData");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  leaveViewMoreId: async (id) => {
    try {
      const response = await apiClient.get(`/getDriverLeaveById/${id}`);
      console.log("API response:", response);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  remarkById: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/approveDriverLeave/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
};
/** Membership Page***/
export const membershipApis = {
  // add: async (formData) => {
  //   try {
  //     Error: Failed to add membership. Please try again.
  //     const response = await apiClient.post("/addMemberShip", formData);
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error adding department:", error);
  //     throw error;
  //   }
  // },
  add: async (formData) => {
    try {
      // Add better debugging to trace the issue
      console.log(
        "Sending membership data:",
        Object.fromEntries([...formData.entries()])
      );

      // Make sure content type is correctly set - try with explicit header
      const response = await apiClient.post("/addMemberShip", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000, // Increase timeout to 30 seconds
      });

      // Log full response for debugging
      console.log("Membership API response:", response);

      return response.data;
    } catch (error) {
      console.error("Error adding membership:", error);

      // Add detailed logging for different error types
      if (error.response) {
        // Server responded with an error status
        console.error("Server error response:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });

        // Return structured error with server details
        return {
          success: false,
          message:
            error.response.data?.message ||
            `Server error ${error.response.status}: ${error.response.data?.code || "Unknown error"
            }`,
          error: error.response.data,
        };
      } else if (error.request) {
        // Request made but no response received
        console.error("No response received:", error.request);
        return {
          success: false,
          message:
            "No response received from server. Please check your connection.",
          error: "NETWORK_ERROR",
        };
      } else {
        // Error in request setup
        return {
          success: false,
          message:
            error.message || "An error occurred while processing the request.",
          error: "REQUEST_SETUP_ERROR",
        };
      }
    }
  },
  addDetails: async (formData) => {
    try {
      const response = await apiClient.post("/AddMembershiplist", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    selection,
    searchValue,
    startDate,
    endDate
  ) => {
    try {
      const response = await apiClient.get("/getMemberShips", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  addIndustry: async (formData) => {
    try {
      const response = await apiClient.post("/addIndustry", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  industryAll: async () => {
    try {
      const response = await apiClient.get("/getIndustry");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  membershipViewMoreId: async (id) => {
    //   try {
    //     const response = await apiClient.post(`/getMenbershipbyId/${id}`);
    //     return response.data;
    //   } catch (error) {
    //     console.error("Error fetching :", error);
    //     throw error;
    //   }
    // },
    try {
      console.log("Requesting membership data for ID:", id);

      if (!id) {
        return {
          success: false,
          message: "Membership ID is required",
          data: null,
        };
      }

      const response = await apiClient.post(`/getMenbershipbyId/${id}`);

      if (response && response.data) {
        return {
          success: true,
          data: response.data,
          message: "Membership data retrieved successfully",
        };
      } else {
        return {
          success: false,
          message: "No data found",
          data: null,
        };
      }
    } catch (error) {
      console.error("Error fetching membership details:", error);

      if (error.response) {
        return {
          success: false,
          message:
            error.response.data?.message ||
            `Server error: ${error.response.status}`,
          data: null,
          status: error.response.status,
        };
      } else {
        return {
          success: false,
          message: error.message || "Unknown error occurred",
          data: null,
        };
      }
    }
  },

  updatebyId: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateMemberShipServer/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getAllWeb: async (page = 1, limit = 5) => {
    try {
      const response = await apiClient.get("/getMembershipForWeb", {
        params: { page, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching Membership Data Web:", error);
      throw error;
    }
  },
};
export const noticeBoardApis = {
  // add: async (formData) => {
  //   try {
  //     const response = await apiClient.post("/addEmployeeShifts", formData);
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error updating:", error);
  //     throw error;
  //   }
  // },
  getAll: async (page = 1, limit = 10, selection, searchValue) => {
    try {
      const response = await apiClient.get("/getNotices", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching:", error);
      throw error;
    }
  },
  // deleteById: async (id) => {
  //   try {
  //     const response = await apiClient.delete(`/deleteEmployeeShift/${id}`);
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error fetching departments:", error);
  //     throw error;
  //   }
  // },
  // editById: async (id) => {
  //   try {
  //     const response = await apiClient.get(`/getEmployeeShiftById/${id}`);
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error fetching departments:", error);
  //     throw error;
  //   }
  // },
  // updateById: async (id, formData) => {
  //   try {
  //     const response = await apiClient.post(
  //       `/updateEmployeeShift/${id}`,
  //       formData
  //     );
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error fetching department by id:", error);
  //     throw error;
  //   }
  // },
};

export const knowledgeCategoryApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addKnowledgeArticles", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getAll: async (id) => {
    try {
      const response = await apiClient.get("/getAllKnowledgeArticles");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteKnowledgeArticle/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
};
/** Login Page***/
export const loginApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/login", formData);
      return response.data;
    } catch (error) {
      console.error("Error Sending OTP:", error);
      throw error;
    }
  },
  verification: async (formData) => {
    try {
      const response = await apiClient.post("/employeeVerification", formData);
      return response.data;
    } catch (error) {
      console.error("Error in Verification:", error);
      throw error;
    }
  },
  getAll: async (id) => {
    try {
      const response = await apiClient.post(`/getcontacts/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteContact/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  SavebyId: async (id, formData) => {
    try {
      const response = await apiClient.post(`/AddCorpDrivers/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
};
/** Client Contact***/
export const clientContactApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addcontacts", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getAll: async (id) => {
    try {
      const response = await apiClient.post(`/getcontacts/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteContact/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  editById: async (id) => {
    try {
      const response = await apiClient.post(`/getContactByid/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  updateById: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateContact/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
};
/** Client Invoice***/
export const clientInvoiceApis = {
  preAddInvoice: async () => {
    try {
      const response = await apiClient.get("/preAddInvoice");
      return response.data;
    } catch (error) {
      console.error("Error fetching pre-add invoice data:", error);
      throw error;
    }
  },
  add: async (item) => {
    try {
      const response = await apiClient.post("/createInvoice", item);
      return response.data;
    } catch (error) {
      console.error("Error adding invoice:", error);
      throw error;
    }
  },
  getAll: async (params = {}) => {
    try {
      const response = await apiClient.get("/getInvoice", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching invoices:", error);
      throw error;
    }
  },
  clientDriverById: async (id) => {
    try {
      const response = await apiClient.get(`/getCorpDriver/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage = {
        message:
          error.response?.data?.message || "Failed to fetch client driver data",
        status: error.response?.status,
        error: error,
      };
      console.error("Error fetching client driver:", errorMessage);
      throw errorMessage;
    }
  },
  driverById: async (id) => {
    try {
      const response = await apiClient.get(`/getDriverDataForPortal/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.post(`/deleteInvoice/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  SavebyId: async (id, formData) => {
    try {
      const response = await apiClient.post(`/AddCorpDrivers/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  updateById: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateInvoice/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating invoice:", error);
      throw error;
    }
  },
};
/** Client Drivers***/
export const clientDriversApis = {
  viewMoreDriver: async (id) => {
    try {
      const response = await apiClient.post(`/getDriverDataForPortal/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getDriver: async (id) => {
    try {
      const response = await apiClient.post(`/getUserByIdOrPhone/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  viewMoreAttendance: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/getDriverAttendance/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getAll: async (
    user_id,
    page = 1,
    limit = 10,
    selection,
    searchValue,
    selectionStatus,
    startDate,
    endDate
  ) => {
    try {
      const response = await apiClient.get(`/getCorpDriver/${user_id}`, {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          ...(selectionStatus && { status: selectionStatus }),
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });
      return { success: true, data: response?.data };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          status: error?.response?.status,
          message: error?.response?.data?.error || "An error occurred",
        };
      } else if (error.request) {
        return {
          success: false,
          status: null,
          message: "No response from the server",
        };
      } else {
        return {
          success: false,
          status: null,
          message: error?.message,
        };
      }
    }
  },
  getClientDriversList: async (
    clientId,
    name,
    user_id
  ) => {
    try {
      const response = await apiClient.post(
        `/getclientDriversList/${clientId}`,
        {},
        {
          params: {
            ...(name && { name }),
            ...(user_id && { user_id }),
          },
        }
      );

      return { success: true, data: response?.data };

    } catch (error) {
      if (error.response) {
        return {
          success: false,
          status: error?.response?.status,
          message: error?.response?.data?.message || "An error occurred",
        };
      } else if (error.request) {
        return {
          success: false,
          status: null,
          message: "No response from the server",
        };
      } else {
        return {
          success: false,
          status: null,
          message: error?.message,
        };
      }
    }
  },

  DriverJoiningDate: async (id, permission, formData) => {
    try {
      const response = await apiClient.post(
        `/updateJoiningData/${permission}/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating joining date:", error);
      throw error;
    }
  },

  SavebyId: async (id, formData) => {
    try {
      const response = await apiClient.post(`/AddCorpDrivers/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateStatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
export const jobCreatedApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10, additionalParams = {}) => {
    try {
      const params = {
        page,
        limit,
        ...additionalParams,
      };

      // Create query string manually to see the exact URL
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');

      // Get the base URL from config
      const baseURL = config.apiBaseUrl || 'http://localhost:3000/public/api';
      const fullUrl = `${baseURL}/getJobsForportal?${queryString}`;

      console.log("📡 jobCreatedApis.getAll called with params:", params);
      console.log("📡 Query string:", queryString);
      console.log("📡 Base URL:", baseURL);
      console.log("📡 Complete API URL:", fullUrl);

      const response = await apiClient.get("/getJobsForportal", {
        params,
      });

      console.log("✅ Laravel API full response:", JSON.stringify(response.data, null, 2));
      console.log("✅ Response structure check:", {
        hasStatus: !!response.data.status,
        status: response.data.status,
        hasMessage: !!response.data.message,
        hasData: !!response.data.data,
        hasRecords: !!response.data.data?.records,
        recordsLength: response.data.data?.records?.length,
        totalPages: response.data.data?.total_pages,
        totalRecords: response.data.data?.total_records,
      });

      // Handle Laravel API structure: { status, message, data: { records, ... } }
      if (response.data?.status === true && response.data?.data) {
        const apiData = response.data.data;
        console.log("✅ Using Laravel API structure");
        console.log("✅ Extracted data:", {
          records: apiData.records ? apiData.records.length : 0,
          totalPages: apiData.total_pages,
          totalRecords: apiData.total_records,
          currentPage: apiData.current_page,
          perPage: apiData.per_page,
        });

        // Check if records array exists and has data
        if (!apiData.records || !Array.isArray(apiData.records)) {
          console.error("❌ Records array not found or not an array:", apiData.records);
          return {
            data: {
              records: [],
              total_pages: apiData.total_pages || 0,
              total_records: apiData.total_records || 0,
              per_page: apiData.per_page || limit,
              current_page: apiData.current_page || page
            }
          };
        }

        return {
          data: {
            records: apiData.records,
            total_pages: apiData.total_pages || 0,
            total_records: apiData.total_records || 0,
            per_page: apiData.per_page || limit,
            current_page: apiData.current_page || page
          }
        };
      }

      // Fallback for unexpected structures
      console.error("❌ Unexpected Laravel API response structure:", response.data);
      console.error("❌ Available keys:", response.data ? Object.keys(response.data) : 'No keys');

      return {
        data: {
          records: [],
          total_pages: 0,
          total_records: 0,
          per_page: limit,
          current_page: page
        }
      };

    } catch (error) {
      console.error("❌ Error fetching jobs:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error status:", error.response?.status);
      console.error("❌ Error message:", error.message);

      // Handle Laravel error responses
      if (error.response?.data) {
        const errorData = error.response.data;
        console.error("❌ Laravel error:", {
          status: errorData.status,
          message: errorData.message,
          error: errorData.error,
        });

        if (errorData.status === false) {
          console.error("❌ Laravel API returned error:", errorData.message);
          throw new Error(errorData.message || 'API error occurred');
        }
      }

      throw error;
    }
  },

  getbyId: async (id) => {
    try {
      const response = await apiClient.get(`/getDriverDataForPortal/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  suspendReason: async (id, formData) => {
    try {
      const response = await apiClient.post(`/suspenduser/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getFleetOwnerVehicles: async (clientId) => {
    try {
      const response = await apiClient.post(`/getFleetOwnerVehicles/${clientId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching fleet owner vehicles:", error);
      throw error;
    }
  },
  createJobWithNotifications: async (jobData) => {
    try {
      const response = await apiClient.post("/createJobWithNotifications", jobData);
      return response.data;
    } catch (error) {
      console.error("Error creating job with notifications:", error);
      throw error;
    }
  },

  getApplicationsForOwner: async (jobId, params = {}) => {
    try {
      const response = await apiClient.post(`/getApplicationsForOwner/${jobId}`, params);
      return response.data.data || response.data;
    } catch (error) {
      console.error("Error fetching applications for owner:", error);
      throw error;
    }
  },

  updateJobApplication: async (applicationId, status) => {
    try {
      const response = await apiClient.post(`/updateJobApplicationStatus/${applicationId}`, {
        Status: status,
        updatedAt: new Date().toISOString(),
      });
      return response.data;
    } catch (error) {
      console.error("Error updating job application:", error);
      throw error;
    }
  },

  rejectJobApplication: async (applicationId, remark) => {
    try {
      const response = await apiClient.post(`/RejecteApplciaiton/${applicationId}`, {
        remark: remark
      });
      return response.data;
    } catch (error) {
      console.error("Error rejecting job application:", error);
      throw error;
    }
  },
};
/****** Clients******/

export const clientApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/AddCorpclient", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    selection,
    searchValue,
    selectionStatus,
    startDate,
    endDate
  ) => {
    try {
      const response = await apiClient.get("/getAllClients", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          client_status: selectionStatus,
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  addIndustry: async (formData) => {
    try {
      const response = await apiClient.post("/addIndustry", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  industryAll: async () => {
    try {
      const response = await apiClient.get("/getIndustry");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  clientViewMoreId: async (id) => {
    try {
      const response = await apiClient.post(`/getClientbyID/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  getbyId: async (id) => {
    try {
      const response = await apiClient.get("/getAllClients", {
        params: {
          user_id: id,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  updateClient: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/UpdateCorpClient/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateClientStatus/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  clientDriverAttendance: async (user_id, finalTime) => {
    try {
      const response = await apiClient.get(
        `/getDriverAttendanceByClientId?client_id=${user_id}&month=${finalTime}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  addDriverAttendance: async (formData) => {
    try {
      const response = await apiClient.post("/AddDriverAttendanceRecord", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding attendance:", error);
      throw error;
    }
  },
  deleteAttendanceRecord: async (formData) => {
    try {
      const response = await apiClient.post("/DeleteAttendanceRecord", formData);
      return response.data;
    } catch (error) {
      console.error("Error deleting attendance:", error);
      throw error;
    }
  },
  updateClientMobileNumber: async (id, phone) => {
    try {
      const response = await apiClient.post(`/updateclientmobilenumber/${id}`, {
        phone: phone
      });
      return response.data;
    } catch (error) {
      console.error("Error updating mobile number:", error);
      throw error;
    }
  },
};
/****** Client Call Records ******/
export const clientCallRecords = {
  getCallRecords: async (filters = {}) => {
    try {
      const response = await apiClient.post("/getCallRecords", filters);
      return response.data;
    } catch (error) {
      console.error("Error fetching call recordings:", error);
      throw error;
    }
  },
  getClientDriverCallRecordings: async (clientId, filters = {}) => {
    try {
      const response = await apiClient.get(`/getClientDriverCallRecordings/${clientId}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching client call recordings:", error);
      throw error;
    }
  },
};
/****** Users******/
/** Crane Operator***/
export const craneOperatorApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getAppUserstoken/users_mech/4");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getbyId: async (id) => {
    // try {
    //   const response = await apiClient.post(`/getOwnerDataByUserId/${id}`);
    //   return response.data;
    // } catch (error) {
    //   console.error("Error fetching department by id:", error);
    //   throw error;
    // }
    try {
      console.log("Requesting fleet owner data for ID:", id);

      if (!id) {
        console.error("Fleet owner ID is undefined or null");
        return {
          success: false,
          message: "Fleet owner ID is required",
          data: null,
        };
      }

      // Use the correct API endpoint for fleet owners
      const response = await apiClient.get(`/getOwnerDataByUserId/${id}`);
      console.log("Fleet owner API response:", response);

      return {
        success: true,
        data: response.data,
        message: "Fleet owner data retrieved successfully",
      };
    } catch (error) {
      console.error("Error fetching fleet owner details:", error);
      if (error.response) {
        return {
          success: false,
          message:
            error.response.data?.message ||
            `Server error: ${error.response.status}`,
          data: null,
          status: error.response.status,
        };
      } else {
        return {
          success: false,
          message: error.message || "Unknown error occurred",
          data: null,
        };
      }
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
/** Crane Owner***/
export const craneOwnerApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getAppUserstoken/users_mech/4");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getbyId: async (id) => {
    try {
      const response = await apiClient.get(`/getOwnerDataByUserId/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
/** Mechanic***/
export const mechanicApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getAppUserstoken/users_mech/4", {
        params: {
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getById: async (id) => {
    try {
      const response = await apiClient.post(`/getOwnerDataByUserId/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
/** Dhaba***/
export const dhabaApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getAppUserstoken/users_dhaba/3", {
        params: {
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getbyId: async (id) => {
    try {
      const response = await apiClient.post(`/getOwnerDataByUserId/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
/** fleetOwner***/
export const fleetOwnerApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getAppUserstoken/users_owner/2", {
        params: {
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getbyId: async (user_id) => {
    try {
      const response = await apiClient.post(`/getOwnerDataByUserId/${user_id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
/** Driver***/
export const DriverApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    selection,
    searchValue,
    selectionStatus,
    startDate,
    endDate
  ) => {
    try {
      const response = await apiClient.get("/getAppUserstoken/users_driver/1", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          ...(selectionStatus && { status: selectionStatus }),
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });
      return { success: true, data: response?.data };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          status: error?.response?.status,
          message: error?.response?.data?.error || "An error occurred",
        };
      } else if (error.request) {
        return {
          success: false,
          status: null,
          message: "No response from the server",
        };
      } else {
        return {
          success: false,
          status: null,
          message: error?.message,
        };
      }
    }
  },

  getbyId: async (id) => {
    try {
      const response = await apiClient.get(`/getDriverDataForPortal/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  suspendReason: async (id, formData) => {
    try {
      const response = await apiClient.post(`/suspenduser/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  getLiveStatus: async ({ tripIds = [], userIds = [] } = {}) => {
    try {
      const params = {};
      if (tripIds && tripIds.length) params.trip_ids = tripIds.join(",");
      if (userIds && userIds.length) params.user_ids = userIds.join(",");
      const response = await apiClient.get("/getDriverLiveStatus", { params });
      return response?.data?.data || {};
    } catch (error) {
      console.error("Error fetching driver live status:", error);
      return {};
    }
  },
  getJobHistory: async (
    driverId,
    { page = 1, limit = 10, status, search, appliedFrom, appliedTo } = {}
  ) => {
    try {
      const params = { page, limit };
      if (status != null && status !== "") params.status = status;
      if (search) params.search = search;
      if (appliedFrom) params.applied_from = appliedFrom;
      if (appliedTo) params.applied_to = appliedTo;
      const response = await apiClient.get(`/getDriverJobHistory/${driverId}`, { params });
      return response?.data || {};
    } catch (error) {
      console.error("Error fetching driver job history:", error);
      return {
        status: false,
        data: {
          records: [],
          total_records: 0,
          total_pages: 0,
          current_page: 1,
          per_page: limit,
        },
      };
    }
  },
};
export const ConveyApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/adduserfronportal", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (pageValue = 1, rowsPerPage = 10) => {
    try {
      const response = await apiClient.get("/ConvoyList", {
        params: {
          page: pageValue,
          limit: rowsPerPage,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching convoy data:", error);
      throw error;
    }
  },

  getbyId: async (id) => {
    try {
      const response = await apiClient.get(`/getDriverDataForPortal/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  suspendReason: async (id, formData) => {
    try {
      const response = await apiClient.post(`/suspenduser/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  changeStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
//Setting of dashboard
/*****Company Settings ******/
export const companySettingsApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get("/getGeneralSettings");
      return response.data;
    } catch (error) {
      console.error("Error fetching company settings:", error);
      throw error;
    }
  },

  post: async (formData) => {
    try {
      const response = await apiClient.post("/updateCompanySettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating company settings:", error);
      throw error;
    }
  },

  AppSettingsUpdate: async (formData) => {
    try {
      const response = await apiClient.post("/AppSettingsUpdate", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating company settings:", error);
      throw error;
    }
  },

  PaymentSettingsUpdate: async (formData) => {
    try {
      const response = await apiClient.post("/updateSettingsPayment", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating company settings:", error);
      throw error;
    }
  },
  googleSettingsUpdate: async (formData) => {
    try {
      const response = await apiClient.post("/updateGoogleSettings", formData);
      return response.data;
    } catch (error) {
      return error;
    }
  },
  employeeGetAll: async () => {
    try {
      const response = await apiClient.post("/updateEmployeedata");
      return response.data;
    } catch (error) {
      console.error("Error fetching company settings:", error);
      throw error;
    }
  },
  employeeUpdate: async (formData) => {
    try {
      const response = await apiClient.post("/updateEmployeedata", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating company settings:", error);
      throw error;
    }
  },
  attendanceGetAll: async () => {
    try {
      const response = await apiClient.post("/updateAttendanceSettings");
      return response.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  },
  attendanceUpdate: async (formData) => {
    try {
      const response = await apiClient.post(
        "/updateAttendanceSettings",
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating data:", error);
      throw error;
    }
  },
};

export const attendanceSettingsApi = {
  getAll: async () => {
    try {
      const response = await apiClient.post("/addAtttencanceSettings");
      return response.data;
    } catch (error) {
      console.error("Error fetching company settings:", error);
      throw error;
    }
  },

  update: async (formData) => {
    try {
      const response = await apiClient.post(
        "/addAtttencanceSettings",
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating company settings:", error);
      throw error;
    }
  },
};
// Finance Settings

export const financeSettingsApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get("/getfinancesettings");
      return response.data;
    } catch (error) {
      console.error("Error fetching finance settings:", error);
      throw error;
    }
  },

  updateinvoice: async (formData) => {
    try {
      const response = await apiClient.post("/updateinvoicesettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },

  updateprefix: async (formData) => {
    try {
      const response = await apiClient.post("/updateprefixsettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },

  getAllInvoicePaymenst: async () => {
    try {
      const response = await apiClient.get("/getallpaymentsettings");
      return response.data;
    } catch (error) {
      console.error("Error fetching finance settings:", error);
      throw error;
    }
  },

  addpaymentsettings: async (formData) => {
    try {
      const response = await apiClient.post("/addpaymentsettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },

  updatepaymentsettings: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updatepaymentsettings/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },

  deletepaymentsettings: async (id, formData) => {
    try {
      const response = await apiClient.delete(
        `/deletepaymentsettings/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },

  getAllCurrency: async () => {
    try {
      const response = await apiClient.get("/getAllCurrency");
      return response.data;
    } catch (error) {
      console.error("Error fetching finance settings:", error);
      throw error;
    }
  },

  addCurrency: async (formData) => {
    try {
      const response = await apiClient.post("/addCurrency", formData);
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },
  updateDefaultCurrency: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateDefaultCurrency/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },

  deleteDefaultCurrency: async (id, formData) => {
    try {
      const response = await apiClient.delete(
        `/deleteDefaultCurrency/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating finance settings:", error);
      throw error;
    }
  },
};

// Profile Settings

export const profileSettingsApi = {
  getById: async (empid) => {
    try {
      const response = await apiClient.post(`/getEmployeesbyID/${empid}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching employee data:", error);
      throw error;
    }
  },
  update: async (empid, formData) => {
    try {
      const response = await apiClient.post(
        `/updateEmployeedata/${empid}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating employee data:", error);
      throw error;
    }
  },
};

/*****App Settings ******/
export const AppSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addVehicleBrands", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getVehicleBrands");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  deletebyId: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteVehicleBrandsByID/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brand by id:", error);
      throw error;
    }
  },
};
/*****Business Settings ******/
export const BusinessSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addBusinesslocation", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/listBusinessLocations", {
        params: {
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateBusinessLocation/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/businesslocationget/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/businesslocationdelete/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
/****Leads Settings*****/
export const LeadSourceSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/AddLeadSource", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/GetLeadSources", {
        params: {
          page,
          limit,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/UpdateLeadSource/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.post(`/GetLeadSource/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/DeleteLeadSource/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const salaryComponentSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createSalaryComponent", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getSalaryComponents");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateSalaryComponent/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getSalaryComponentById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteSalaryComponent/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const salaryGroupSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createSalaryGroup", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getSalaryGroups");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateSalaryGroup/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getSalaryGroupById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteSalaryGroup/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const salaryTDSSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createSalaryTDS", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getSalaryTDS");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateSalaryTDS/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getSalaryTDSById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteSalaryTDS/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const paymentMethodSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createPaymentMethod", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getPaymentMethods");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updatePaymentMethod/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getPaymentMethodById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deletePaymentMethod/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const designationHRApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addDesignation", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getDesignation");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateDesignation/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.post(`/getDesignationById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteDesignation/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const departmentHRApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addDepartment", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getDepartments");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateDepartment/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.post(`/getDepartmentById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteDepartment/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const taxSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addTaxSettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getTaxSettings");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateTaxSettings/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.post(`/getTaxSettingsById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteTaxSettings/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const LeadCategorySettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/AddLeadCategory", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/GetLeadCategories", {
        params: {
          page,
          limit,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/UpdateLeadCategory/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/GetLeadCategory/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/DeleteLeadCategory/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const LeadAgentsSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/AddLeadAgents", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/GetLeadAgents", {
        params: {
          page,
          limit,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  getEmployee: async () => {
    try {
      const response = await apiClient.get("/serveUsers/2");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/UpdateLeadSource/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.post(`/GetLeadSource/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/DeleteLeadAgents/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const AgentSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createticketagent", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getticketagents");

      return response.data;
    } catch (error) {
      console.error("Error fetching :", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateticketagent/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getAgentsByGroupId/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating :", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteticketagent/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const groupSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post(
        "/addTicketGroupSettings",
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getTicketGroupSettings");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateTicketGroupSettings/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.post(
        `/getTicketGroupSettingsById/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(
        `/deleteTicketGroupSettings/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const typeSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createType", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getalltypes");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateType/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/gettickettypebyid/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteType/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const channelSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createchannel", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getalltchannel");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updatechannel/${id}`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getticketchannelbyid/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deletechannel/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
export const statusProjectSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createprojectSettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.post("/updateProjectSettings");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (formData) => {
    try {
      const response = await apiClient.post(`/updateProjectSettings`, formData);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getProjectSettingsById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteProjectSettings/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
  getStatusAll: async () => {
    try {
      const response = await apiClient.get("/getProjectSettings/1");

      return response.data;
    } catch (error) {
      console.error("Error fetching:", error);
      throw error;
    }
  },

  updateStatus: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateProjectSettings/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating :", error);
      throw error;
    }
  },
};
export const categoryProjectSettingsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createprojectSettings", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getProjectSettings/2");

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },

  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateProjectSettings/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  edit: async (id) => {
    try {
      const response = await apiClient.get(`/getProjectSettingsById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteProjectSettings/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting :", error);
      throw error;
    }
  },
};
// Vehicle Brands API
export const vehicleBrandsApi = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addVehicleBrands", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    selection,
    searchValue,
    startDate,
    endDate
  ) => {
    try {
      const response = await apiClient.get("/getVehicleBrands", {
        params: {
          page,
          limit,
          ...(selection && { [selection]: searchValue }),
          ...(startDate &&
            endDate && { timestamps: `${startDate},${endDate}` }),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  deletebyId: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteVehicleBrandsByID/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brand by id:", error);
      throw error;
    }
  },
};

// Vehicle  model API
export const VehicleModelsApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addVehicleModel", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      throw error;
    }
  },
  getAll: async () => {
    try {
      const response = await apiClient.get("/getVehicleModels");
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  deletebyId: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteVehicleModelById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brand by id:", error);
      throw error;
    }
  },
};
//Department api
export const DepartmentApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addDepartment", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getDepartments", {
        params: {
          page,
          limit,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getDepartmentName: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getDepartmentNames", {
        params: {
          page,
          limit,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  getParentValue: async () => {
    try {
      const response = await apiClient.get("/getDepartmentNames");
      return response.data;
    } catch (error) {
      console.error("Error fetching Departments:", error);
      throw error;
    }
  },
  deletebyId: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteDepartment/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateDepartment/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
//Designation api
export const DesignationApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addDesignation", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  getAll: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getDesignation", {
        params: {
          page,
          limit,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching corp drivers:", error);
      throw error;
    }
  },

  getParentValue: async () => {
    try {
      const response = await apiClient.get("/getDesignationNames");
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  deletebyId: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteDesignation/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching department by id:", error);
      throw error;
    }
  },
  update: async (id, formData) => {
    try {
      const response = await apiClient.post(
        `/updateDesignation/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  },
};
//Jobs api
export const JobsApis = {
  getAll: async () => {
    try {
      const response = await apiClient.get("/getJobDetails");
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brands:", error);
      throw error;
    }
  },
  closebyId: async (id) => {
    try {
      const response = await apiClient.get(`/changestatusofjob/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle brand by id:", error);
      throw error;
    }
  },
};

// Ticket APIs

// Live Tracking API

export const liveTrackingApi = {
  startTracking: (userId, callback) => {
    let keepTracking = true;

    const track = async () => {
      while (keepTracking) {
        try {
          const data = await liveTrackingApi.getLatest(userId);
          callback(data);
        } catch (error) {
          console.error("Error fetching live tracking data:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
    };

    track();

    return () => {
      keepTracking = false;
    };
  },

  getLatest: async (userId) => {
    try {
      const response = await apiClient.get(`/getLatestLocation/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

//new implements

// BLOGS API

export const blogApis = {
  getAllByType: async (type, additionalParams = {}) => {
    try {
      // Construct query string from additional parameters
      const queryParams = new URLSearchParams({
        ...additionalParams,
      }).toString();

      const response = await apiClient.get(
        `/getAllblogs/${type}?${queryParams}`
      );
      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching blogs by type:", error);
      throw error;
    }
  },

  // Get Single Blog by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/blogs/${id}`);
      console.log("resonse for id", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching blog by ID:", error);
      throw error;
    }
  },

  // Update Blog by ID
  updateById: async (id, formData) => {
    try {
      console.log("trying to update blog for ID:", id);
      const response = await apiClient.post(`/resouces/${id}`, formData);
      console.log("Response: ", response);

      return response.data;
    } catch (error) {
      console.log("Error updating blog:", error);
      throw error;
    }
  },

  // Delete Blog by ID
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/resouces/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting blog:", error);
      throw error;
    }
  },

  // Get Blogs for Dashboard
  getDashboardBlogs: async (additionalParams = {}) => {
    try {
      // Correctly construct query string from additionalParams
      const queryParams = new URLSearchParams(additionalParams).toString();
      console.log("The param is: ", `/dashboard/resouces?${queryParams}`);

      const response = await apiClient.get(
        `/dashboard/resouces?${queryParams}` // Use single `?` for query parameters
      );
      console.log("API Response:", response); // Log response for debugging
      return response.data;
    } catch (error) {
      console.log("Error fetching dashboard blogs:", error);
      throw error;
    }
  },

  // // Add Blog
  // add: async (formData) => {
  //   try {
  //     const response = await apiClient.post("/createresources", formData);
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error adding blog:", error);
  //     throw error;
  //   }
  // },

  // Add Blogs from Dashboard

  // addBlogsFromDashboard: async (formData) => {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addblogsfromDashboard", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding blogs from dashboard:", error);
      throw error;
    }
  },
};

//LANDING API

export const siteSettings = {
  getSettingsForWebsite: async () => {
    try {
      const response = await apiClient.get("/getsettingsforweb");
      return response.data;
    } catch (error) {
      console.error("Error fetching website settings:", error);
      throw error;
    }
  },

  getDriversForWeb: async (page = 1, perPage = 8, filters = {}) => {
    try {
      const params = new URLSearchParams();

      // Add base pagination params
      params.append("page", page);
      params.append("per_page", perPage);

      // Add all filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      console.log(
        "Params for drivers for web: ",
        Object.fromEntries(params.entries())
      ); // Log params for debugging

      const response = await apiClient.get(
        `/getDriversForWeb?${params.toString()}`
      );
      console.log("Response for drivers for web: ", response.data); // Log response for debugging
      return response.data;
    } catch (error) {
      console.error("Error fetching drivers for web:", error);
      throw error;
    }
  },
};

// FEATURE APIS

export const featureApis = {
  // Get All Features
  getAll: async (additionalParams = {}) => {
    try {
      // Construct query string from additional parameters
      const queryParams = new URLSearchParams(additionalParams).toString();

      const response = await apiClient.get(
        `/getAllFeatures${queryParams ? `?${queryParams}` : ""}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all features:", error);
      throw error;
    }
  },

  // Create a Feature
  add: async (formData) => {
    try {
      const response = await apiClient.post("/createFeature", formData);
      return response.data;
    } catch (error) {
      console.error("Error creating feature:", error);
      throw error;
    }
  },

  // Get Single Feature by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/getFeatureById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching feature by ID:", error);
      throw error;
    }
  },

  // Update Feature by ID
  updateById: async (id, formData) => {
    try {
      const response = await apiClient.post(`/updateFeature/${id}`, formData);
      return response.data;
    } catch (error) {
      console.log("Error updating feature:", error);
      throw error;
    }
  },

  // Delete Feature by ID
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteFeature/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting feature:", error);
      throw error;
    }
  },

  // Get Features for Dashboard
  getDashboardFeatures: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      console.log("Query Params:", queryParams);
      const response = await apiClient.get(
        `/getFeaturesForDashboard?${queryParams}`
      );

      console.log("dash API: ", response); // Log response for debugging

      return response.data;
    } catch (error) {
      console.error("Error fetching dashboard features:", error);
      throw error;
    }
  },
};

// FAQ APIS

export const faqApis = {
  // Get all FAQs with optional query parameters
  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/getAllFaqs?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching all FAQs:", error);
      throw error;
    }
  },

  // Create a new FAQ
  add: async (faqData) => {
    try {
      const response = await apiClient.post("/createFaq", faqData);
      return response.data;
    } catch (error) {
      console.error("Error creating FAQ:", error);
      throw error;
    }
  },

  // Get a single FAQ by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/getFaqById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching FAQ by ID:", error);
      throw error;
    }
  },

  // Update an FAQ by ID
  updateById: async (id, faqData) => {
    try {
      const response = await apiClient.post(`/updateFaq/${id}`, faqData);
      return response.data;
    } catch (error) {
      console.error("Error updating FAQ:", error);
      throw error;
    }
  },

  // Delete an FAQ by ID
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteFaq/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      throw error;
    }
  },

  // Get FAQs for the dashboard with optional query parameters
  getDashboardFaqs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      console.log("Query Params:", queryParams);
      const response = await apiClient.get(
        `/getFaqsForDashboard?${queryParams}`
      );
      console.log("FAQ res", response);
      return response.data;
    } catch (error) {
      console.error("Error fetching FAQs for dashboard:", error);
      throw error;
    }
  },
};

// Testimonial APIS

export const testimonialApis = {
  // Get all Testimonials with optional query parameters
  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(
        `/getAllTestimonials?${queryParams}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all Testimonials:", error);
      throw error;
    }
  },

  // Create a new Testimonial
  add: async (testimonialData) => {
    try {
      const response = await apiClient.post(
        "/createTestimonial",
        testimonialData
      );
      return response.data;
    } catch (error) {
      console.log("Error creating Testimonial:", error);
      throw error;
    }
  },

  // Get a single Testimonial by ID
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/getTestimonialById/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Testimonial by ID:", error);
      throw error;
    }
  },

  // Update a Testimonial by ID
  updateById: async (id, testimonialData) => {
    try {
      console.log("Trying to update Testimonial for ID:", id);
      const response = await apiClient.post(
        `/updateTestimonial/${id}`,
        testimonialData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Testimonial:", error);
      throw error;
    }
  },

  // Delete a Testimonial by ID
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteTestimonial/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting Testimonial:", error);
      throw error;
    }
  },

  // Get Testimonials for the dashboard with optional query parameters
  getDashboardTestimonials: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(
        `/getTestimonialsForDashboard?${queryParams}`
      );
      return response.data;
    } catch (error) {
      console.log("Error fetching Testimonials for dashboard:", error);
      throw error;
    }
  },
};
export const rolesSettingsApis = {
  getAll: async () => {
    try {
      const response = await apiClient.get("/getRoles");
      return response.data;
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/getRoles/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching role by ID:", error);
      throw error;
    }
  },

  add: async (formData) => {
    try {
      const response = await apiClient.post("/addRole", formData);
      return response.data;
    } catch (error) {
      console.error("Error adding role:", error);
      throw error;
    }
  },

  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteRoles/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting role:", error);
      throw error;
    }
  },

  changeStatus: async (id) => {
    try {
      const response = await apiClient.post(`/changestatus/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error changing role status:", error);
      throw error;
    }
  },
};
export const recruitJobsApis = {
  getAllJobTitle: async (params = {}) => {
    try {
      // Convert params object to URL query parameters
      const queryParams = new URLSearchParams(params).toString();
      const url = `/getJobsList/permission${queryParams ? `?${queryParams}` : ""
        }`;

      const response = await apiClient.get(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching job titles:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch job titles",
        error: error,
      };
    }
  },
  getAllJobCategories: async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = new URLSearchParams();

      // Add pagination parameters
      params.append("page", page);
      params.append("limit", limit);

      // Add any additional filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await apiClient.get(
        `/getJobCategories?${params.toString()}`
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching recruitment jobs:", error);
    }
  },
  getAllJobSubCategories: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/getJobSubCategories${queryParams ? `?${queryParams}` : ""}`;

      const response = await apiClient.get(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching job subcategories:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch job subcategories",
        error: error,
      };
    }
  },
  getSkills: async () => {
    try {
      const response = await apiClient.get("/skills/list");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching skills:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch skills",
        error: error,
      };
    }
  },
  getInterviewRounds: async () => {
    try {
      const response = await apiClient.get("/interview-round/list");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching interview rounds:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch interview rounds",
        error: error,
      };
    }
  },
  getWorkExperience: async () => {
    try {
      const response = await apiClient.get("/work-exp/list");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching work experience:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch work experience",
        error: error,
      };
    }
  },
  getJobTypes: async () => {
    try {
      const response = await apiClient.get("/job-type/list");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching job types:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch job types",
        error: error,
      };
    }
  },
  //CREATE or ADDERS
  addJob: async (jobData) => {
    try {
      const formData = new FormData();

      // Add all fields to FormData
      Object.keys(jobData).forEach((key) => {
        const value = jobData[key];

        // Handle array fields by converting to JSON string
        if (
          ["skills", "location_data", "interview_rounds"].includes(key) &&
          Array.isArray(value)
        ) {
          formData.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const response = await apiClient.post("/addRjob/permission", formData);

      return {
        success: true,
        data: response.data,
        message: "Job added successfully",
      };
    } catch (error) {
      console.error("Error adding job:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to add job",
        error: error,
      };
    }
  },
  addJobCategory: async (jobCategory) => {
    try {
      const response = await apiClient.post(
        "/createJobCategory/permission",
        jobCategory
      );

      return {
        success: true,
        data: response.data,
        message: "Job category added successfully",
      };
    } catch (error) {
      console.error("Error adding job category:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to add job category",
        error: error,
      };
    }
  },
  addJobSubCategory: async (jobCategory) => {
    try {
      const response = await apiClient.post(
        "/createJobSubCategory/permission",
        jobCategory
      );

      return {
        success: true,
        data: response.data,
        message: "Job category added successfully",
      };
    } catch (error) {
      console.error("Error adding job category:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to add job category",
        error: error,
      };
    }
  },
  addWorkExperience: async (work) => {
    try {
      const response = await apiClient.post("/work-exp/add/permission", work);

      return {
        success: true,
        data: response.data,
        message: "Work experience added successfully",
      };
    } catch (error) {
      console.error("Error adding work experience:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to add work experience",
        error: error,
      };
    }
  },
  addJobType: async (jobType) => {
    try {
      const response = await apiClient.post(
        "/job-type/add/permission",
        jobType
      );

      return {
        success: true,
        data: response.data,
        message: "Job type added successfully",
      };
    } catch (error) {
      console.error("Error adding job type:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to add job type",
        error: error,
      };
    }
  },
  //DELETERS
  deleteJobCategory: async (id) => {
    try {
      const response = await apiClient.delete(
        `/getJobCategoryById/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Job category deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting job category:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to delete job category",
        error: error,
      };
    }
  },
  deleteJobSubCategory: async (id) => {
    try {
      const response = await apiClient.delete(
        `/getJobSubCategoryById/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Job sub category deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting job sub category:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to delete job sub category",
        error: error,
      };
    }
  },
  deleteJobType: async (id) => {
    try {
      const response = await apiClient.delete(
        `/job-type/delete/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Job type deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting job type category:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to delete job type",
        error: error,
      };
    }
  },
  deleteWorkExperience: async (id) => {
    try {
      const response = await apiClient.delete(
        `/work-exp/delete/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Work experience deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting Work experience:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to delete Work experience",
        error: error,
      };
    }
  },
  updateJob: async (id, jobData) => {
    try {
      const formData = new FormData();

      // Add all fields to FormData
      Object.keys(jobData).forEach((key) => {
        const value = jobData[key];

        // Handle array fields by converting to JSON string
        if (
          ["skills", "location_data", "interview_rounds"].includes(key) &&
          Array.isArray(value)
        ) {
          formData.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const response = await apiClient.post(
        `/updateRjob/${id}/permission`,
        formData
      );

      return {
        success: true,
        data: response.data,
        message: "Job updated successfully",
      };
    } catch (error) {
      console.error("Error updating job:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to update job",
        error: error,
      };
    }
  },
  getAllWeb: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get("/getJobsForWeb", {
        params: {
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
};

export const recruitSkillsApis = {
  getSkills: async (params = {}) => {
    try {
      // Convert params object to URL query parameters
      const queryParams = new URLSearchParams(params).toString();
      const url = `/skills/list${queryParams ? `?${queryParams}` : ""}`;

      const response = await apiClient.get(url);
      console.log("url for skills: ", url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching skills:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch skills",
        error: error,
      };
    }
  },
  // Add a new skill
  addSkill: async (skillData) => {
    try {
      const response = await apiClient.post(
        "/skills/add/permission",
        skillData
      );

      return {
        success: true,
        data: response.data,
        message: "Skill added successfully",
      };
    } catch (error) {
      console.error("Error adding skill:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to add skill",
        error: error,
      };
    }
  },
  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(
        `/skills/delete/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Skill deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting skill:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to delete skill",
        error: error,
      };
    }
  },
  updateById: async (id, name) => {
    try {
      const response = await apiClient.post(
        `/skills/update/${id}/permission`,
        name
      );

      return {
        success: true,
        data: response.data,
        message: "Skill updated successfully",
      };
    } catch (error) {
      console.error("Error updating skill:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to update skill",
        error: error,
      };
    }
  },
};

export const recruitJobApplicationApis = {
  add: async (applicationData) => {
    try {
      const response = await apiClient.post(
        "/addRApplication/permission",
        applicationData
      );
      return {
        success: true,
        data: response.data,
        message: "Job application added successfully",
      };
    } catch (error) {
      console.error("Error adding job application:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to add job application",
        error: error,
      };
    }
  },
  update: async (applicationData) => {
    try {
      const response = await apiClient.put(
        "/updateRApplication/permission",
        applicationData
      );
      return {
        success: true,
        data: response.data,
        message: "Job application updated successfully",
      };
    } catch (error) {
      console.error("Error updating job application:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to update job application",
        error: error,
      };
    }
  },

  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/getRApplicationsList/permission${queryParams ? `?${queryParams}` : ""
        }`;

      const response = await apiClient.get(url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching job applications:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch job applications",
        error: error,
      };
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(
        `/getRApplicationDetails/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Job application details retrieved successfully",
      };
    } catch (error) {
      console.error("Error fetching job application details:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          "Failed to fetch job application details",
        error: error,
      };
    }
  },

  deleteById: async (id) => {
    try {
      const response = await apiClient.delete(
        `/deleteRApplication/${id}/permission`
      );

      return {
        success: true,
        data: response.data,
        message: "Job application deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting job application:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to delete job application",
        error: error,
      };
    }
  },
};

export const ticketApis = {
  getTickets: async (params = {}) => {
    try {
      // Convert params object to URL query parameters
      const queryParams = new URLSearchParams(params).toString();
      const url = `/getTickets${queryParams ? `?${queryParams}` : ""}`;

      const response = await apiClient.get(url);
      console.log("url for tickets: ", url);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching skills:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch skills",
        error: error,
      };
    }
  },

  getTicketonDashboard: async (params = {}) => {
    try {
      const response = await apiClient.get("/getTicketonDashboard", { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching ticket dashboard data:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch tickets",
        error: error,
      };
    }
  },

  getOpenTicketsCount: async () => {
    try {
      // Use the same endpoint as the tickets page
      const response = await apiClient.get("/getTicketonDashboard");

      if (response.success && response.data && Array.isArray(response.data.tickets)) {
        // Count tickets with status "1" (Open)
        const openTickets = response.data.tickets.filter(
          ticket => ticket.status === 1 || ticket.status === "1"
        );
        return {
          success: true,
          count: openTickets.length,
        };
      }
      return {
        success: true,
        count: 0,
      };
    } catch (error) {
      console.error("Error fetching open tickets count:", error);
      return {
        success: false,
        count: 0,
        error: error,
      };
    }
  },

  createTicket: async (ticketData) => {
    try {
      const response = await apiClient.post("/createtickets", ticketData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Laravel returns '1602' for success, '1603' for failure
      if (response.data === '1602') {
        return {
          success: true,
          data: response.data,
          message: "Ticket created successfully",
        };
      } else if (response.data === '1603') {
        return {
          success: false,
          message: "Failed to create ticket. Please try again.",
          error: response.data,
        };
      } else {
        return {
          success: true, // Assume success if we get here
          data: response.data,
          message: "Ticket created successfully",
        };
      }
    } catch (error) {
      console.error("Error creating ticket:", error);

      // Handle validation errors from Laravel (status 422)
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        return {
          success: false,
          message: errorMessages.join(", ") || "Validation failed",
          error: errors,
        };
      }

      return {
        success: false,
        message: error?.response?.data?.message || "Failed to create ticket",
        error: error,
      };
    }
  },

  updateTicket: async (ticketId, ticketData) => {
    try {
      console.log('Updating ticket ID:', ticketId);

      // Check if token is available
      const state = store.getState();
      const storeToken = state.user?.token;
      console.log('Token from store:', storeToken ? 'Present' : 'Missing');

      const response = await apiClient.post(`/updateTickets/${ticketId}`, ticketData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Update ticket API response:', response);
      console.log('Response data structure:', response.data);

      // Laravel returns code '1602' for success, '1603' for failure
      if (response.data.code === '1602') {
        return {
          success: true,
          data: response.data,
          message: response.data.message || "Ticket updated successfully",
        };
      } else {
        return {
          success: false,
          message: response.data.message || "Failed to update ticket",
          error: response.data,
        };
      }
    } catch (error) {
      console.error("Error updating ticket:", error);
      console.error("Error response status:", error.response?.status);
      console.error("Error response data:", error.response?.data);

      // Handle 401 specifically
      if (error.response?.status === 401) {
        return {
          success: false,
          message: "Authentication failed. Please login again.",
          error: error,
        };
      }

      // Handle validation errors from Laravel (status 422)
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        return {
          success: false,
          message: errorMessages.join(", ") || "Validation failed",
          error: errors,
        };
      }

      // Handle 404 - ticket not found
      if (error.response?.status === 404) {
        return {
          success: false,
          message: "Ticket not found",
          error: error,
        };
      }

      return {
        success: false,
        message: error?.response?.data?.message || "Failed to update ticket",
        error: error,
      };
    }
  },

  getTicketById: async (ticketId) => {
    try {
      console.log('Fetching ticket details for ID:', ticketId);

      // Check if token is available
      const state = store.getState();
      const storeToken = state.user?.token;
      console.log('Token from store:', storeToken ? 'Present' : 'Missing');

      const response = await apiClient.get(`/getTicketById/${ticketId}`);
      console.log('getTicketById API response:', response);
      console.log('Response data structure:', response.data);

      // Axios wraps the API response in response.data
      if (response.data?.success && response.data?.data) {
        return {
          success: true,
          data: response.data.data,
        };
      }
      return {
        success: false,
        message: response.data?.message || "Failed to fetch ticket",
      };
    } catch (error) {
      console.error("Error fetching ticket by ID:", error);
      console.error("Error response status:", error.response?.status);
      console.error("Error response data:", error.response?.data);

      // Handle 401 specifically
      if (error.response?.status === 401) {
        return {
          success: false,
          message: "Authentication failed. Please login again.",
          error: error,
        };
      }

      return {
        success: false,
        message: "Failed to fetch ticket",
        error: error,
      };
    }
  },

  getBusinessLocationById: async (id) => {
    try {
      const response = await apiClient.get(`/businesslocationget/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching business location by ID:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch business location",
        error: error,
      };
    }
  },

  deleteTicketById: async (id) => {
    try {
      const response = await apiClient.delete(`/deleteTickets/${id}`);
      return {
        success: true,
        data: response.data,
        message: "Ticket deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting ticket:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to delete ticket",
        error: error,
      };
    }
  },

  getTicketTypes: async () => {
    try {
      const response = await apiClient.get("/getalltypes");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching ticket types:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch ticket types",
        error: error,
      };
    }
  },

  getTicketAgents: async () => {
    try {
      const response = await apiClient.get("/getticketagents");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching ticket agents:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch ticket agents",
        error: error,
      };
    }
  },

  getAgentsByGroupId: async (groupId) => {
    try {
      const response = await apiClient.get(`/getAgentsByGroupId/${groupId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching agents by group ID:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch agents by group",
        error: error,
      };
    }
  },

  getTicketGroups: async () => {
    try {
      const response = await apiClient.get("/getTicketGroupSettings");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching ticket groups:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch ticket groups",
        error: error,
      };
    }
  },

  getTicketChannel: async () => {
    try {
      const response = await apiClient.get("/getalltchannel");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching ticket channels:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch ticket channels",
        error: error,
      };
    }
  },

  getprerequisiteDataForTicketCreation: async (requestType) => {
    try {
      const response = await apiClient.get(`/getprerequisiteDataForTicketCreation/${requestType}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching prerequisite data for ticket creation:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch prerequisite data",
        error: error,
      };
    }
  },

  getTicketHistory: async (ticketId, page = 1, limit = 10) => {
    try {
      console.log(`Fetching ticket history for ID: ${ticketId}, Page: ${page}, Limit: ${limit}`);

      // Check if token is available
      const state = store.getState();
      const storeToken = state.user?.token;
      console.log('Token from store:', storeToken ? 'Present' : 'Missing');

      const response = await apiClient.get(`/getTicketHistory/${ticketId}`, {
        params: { page, limit }
      });
      console.log('getTicketHistory API response:', response);
      console.log('Response data structure:', response.data);

      // New API structure: { success: true, agent: {...}, pagination: {...}, data: [...] }
      if (response.data?.success && response.data?.data) {
        return {
          success: true,
          data: {
            agent: response.data.agent || null,
            pagination: response.data.pagination || {},
            data: Array.isArray(response.data.data) ? response.data.data : [],
          },
        };
      }
      return {
        success: false,
        message: response.data?.message || "Failed to fetch ticket history",
      };
    } catch (error) {
      console.error("Error fetching ticket history:", error);
      console.error("Error response status:", error.response?.status);
      console.error("Error response data:", error.response?.data);

      // Handle 401 specifically
      if (error.response?.status === 401) {
        return {
          success: false,
          message: "Authentication failed. Please login again.",
          error: error,
        };
      }

      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to fetch ticket history",
        error: error,
      };
    }
  },

  addTicketHistory: async (formData) => {
    try {
      console.log('Adding ticket history:', formData);
      const response = await apiClient.post('/addTicketHistory', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('addTicketHistory API response:', response);

      if (response.data.success || response.data.code === '1602') {
        return {
          success: true,
          data: response.data,
          message: response.data.message || "Reply sent successfully",
        };
      } else {
        console.error('addTicketHistory returned error:', response);
        return {
          success: false,
          message: response.data.message || "Failed to send reply",
          error: response.data,
        };
      }
    } catch (error) {
      console.error("Error adding ticket history:", error);

      // Handle validation errors from Laravel (status 422)
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        return {
          success: false,
          message: errorMessages.join(", ") || "Validation failed",
          error: errors,
        };
      }

      return {
        success: false,
        message: error?.response?.data?.message || "Failed to send reply",
        error: error,
      };
    }
  },

  closeTicketByClient: async (ticketId) => {
    try {
      console.log('Closing ticket:', ticketId);
      const response = await apiClient.post('/closeTicketByClient', { ticket_id: ticketId });
      console.log('closeTicketByClient API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Ticket closed successfully",
      };
    } catch (error) {
      console.error("Error closing ticket:", error);
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to close ticket",
        error: error,
      };
    }
  },

  getAgentDetails: async (empid) => {
    try {
      const response = await apiClient.get(`/getEmployeesByID_agnet/${empid}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Error fetching agent details:", error);
      return {
        success: false,
        message: error?.response?.data?.message || "Failed to fetch agent details",
        error: error,
      };
    }
  },
};

export const craneBooking = {
  add: async (formData) => {
    try {
      // Convert params object to URL query parameters

      const response = await apiClient.post("addCraneBooking", formData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching crane operators:", error);
      return {
        success: false,
        message: error?.message || "Failed to fetch crane operators",
        error: error,
      };
    }
  },
};

export const convoyApis = {
  add: async (formData) => {
    try {
      const response = await apiClient.post("/addConvoy", formData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error adding convoy:", error);
      return {
        success: false,
        message: error?.message || "Failed to add convoy",
        error: error,
      };
    }
  },
};
export const driverRequestsApis = {
  add: async (formData, permission = "permission") => {
    try {
      const response = await apiClient.post(
        `/driver-requests/add/${permission}`,
        formData
      );
      return {
        success: true,
        data: response.data,
        message: "Driver request added successfully",
      };
    } catch (error) {
      console.error("Error adding driver request:", error);
      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message:
            error.response.data?.message || "Failed to add driver request",
          error: error.response.data,
        };
      } else {
        return {
          success: false,
          message: error.message || "Network error occurred",
          error,
        };
      }
    }
  },
  getAll: async (
    page = 1,
    limit = 10,
    filters = {},
    permission = "permission"
  ) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        ...filters,
      });

      const response = await apiClient.get(
        `/driver-requests/list/${permission}?${params.toString()}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching driver requests:", error);
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to fetch driver requests",
        error,
      };
    }
  },
  getById: async (id, permission = "permission") => {
    try {
      const response = await apiClient.get(
        `/driver-requests/view/${id}/${permission}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching driver request details:", error);
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message ||
          "Failed to fetch driver request details",
        error,
      };
    }
  },
  update: async (id, formData, permission = "permission") => {
    try {
      const response = await apiClient.put(
        `/driver-requests/edit/${id}/${permission}`,
        formData
      );
      return {
        success: true,
        data: response.data,
        message: "Driver request updated successfully",
      };
    } catch (error) {
      console.error("Error updating driver request:", error);
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to update driver request",
        error,
      };
    }
  },

  updateAssignedTo: async (id, formData, permission = "permission") => {
    try {
      const response = await apiClient.patch(
        `/driver-requests/assign/${id}/${permission}`,
        formData
      );
      return {
        success: true,
        data: response.data,
        message: "Driver request assigned successfully",
      };
    } catch (error) {
      console.error("Error assigning driver request:", error);
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to assign driver request",
        error,
      };
    }
  },

  // Update the status of a driver request
  updateStatus: async (id, formData, permission = "permission") => {
    try {
      const response = await apiClient.patch(
        `/driver-requests/status/${id}/${permission}`,
        formData
      );
      return {
        success: true,
        data: response.data,
        message: "Driver request status updated successfully",
      };
    } catch (error) {
      console.error("Error updating driver request status:", error);
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message ||
          "Failed to update driver request status",
        error,
      };
    }
  },
};

export const SuspendedComment = {
  get: async (id) => {
    try {
      const response = await apiClient.get(`comments/portal/${id}`);

      // Log the raw response for debugging
      console.log("SuspendedComment API response:", response.data);

      // Return the array directly
      return response.data || [];
    } catch (error) {
      console.error("Error fetching suspended comments:", error);
      return [];
    }
  },
  AddTimeline: async (formData) => {
    try {
      console.log("🚀 Sending addTimeline:", Object.fromEntries(formData));

      const response = await apiClient.post(`addTimelinebyProtal`, formData);

      console.log("✅ Timeline API response:", response.data);

      return {
        success: true,
        data: response.data,
        message: "Timeline created successfully",
      };
    } catch (error) {
      console.error("❌ Error adding timeline:", error);

      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to add timeline",
        error,
      };
    }
  },

};


export const TripsApi = {

  createTripFromDashboard: async (payload) => {
    try {
      const response = await apiClient.post(
        `/createtripformdashboard`,
        payload
      );

      return {
        success: true,
        data: response?.data,
      };

    } catch (error) {
      if (error.response) {
        return {
          success: false,
          status: error?.response?.status,
          message: error?.response?.data?.message || "An error occurred",
        };
      } else if (error.request) {
        return {
          success: false,
          status: null,
          message: "No response from the server",
        };
      } else {
        return {
          success: false,
          status: null,
          message: error?.message,
        };
      }
    }
  },
  /**
   * Get trips for dashboard with filters & pagination
   * @param {string} permission
   * @param {object} params - filters & pagination
   */
  getTripsForDashboard: async (permission, params = {}) => {
    try {
      console.log("🚀 Fetching trips with params:", params);

      const response = await apiClient.post(
        `getTripsforDashboard/${permission}`,
        params
      );

      console.log("✅ Trips API response:", response.data);

      return {
        success: true,
        data: response.data?.data || [],
        totalRecords: response.data?.total_records || 0,
        totalPages: response.data?.total_pages || 0,
        currentPage: response.data?.current_page || 1,
        message: response.data?.message || "Trips fetched successfully",
      };
    } catch (error) {
      console.error("❌ Error fetching trips:", error);

      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to fetch trips",
        data: [],
      };
    }
  },
  /**
   * Get detailed performance analysis for a trip with cache
   * @param {string} applicant_id
   * @param {string} trip_id
   * @param {string} timezone - Local system timezone (e.g., "America/New_York")
   */
  getPerformanceAnalysis: async (applicant_id, trip_id, timezone) => {
    try {
      console.log("🚀 Fetching performance analysis with cache:", { applicant_id, trip_id, timezone });

      const response = await apiClient.post(
        'showPerformanceAnalysisWithCache',
        { applicant_id, trip_id, timezone }
      );

      console.log("✅ Performance analysis response:", response.data);

      return {
        success: true,
        data: response.data || {},
        message: response.data?.message || "Performance analysis fetched successfully",
      };
    } catch (error) {
      console.error("❌ Error fetching performance analysis:", error);

      return {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.message || "Failed to fetch performance analysis",
        data: {},
      };
    }
  },

  /**
   * Get daily stats for a trip (IST timezone optimized)
   * @param {string} applicant_id
   * @param {string} trip_id
   * @param {string} timezone - Local system timezone (e.g., "America/Los_Angeles")
   */
  getDailyStats: async (applicant_id, trip_id, timezone) => {
    try {
      console.log("🚀 Fetching daily stats:", { applicant_id, trip_id, timezone });

      const response = await apiClient.post(
        'showPerformanceAnalysisWithCacheIST',
        { applicant_id, trip_id, timezone }
      );

      console.log("✅ Daily stats response:", response.data);

      return {
        success: true,
        data: response.data || {},
        message: response.data?.message || "Daily stats fetched successfully",
      };
    } catch (error) {
      console.error("❌ Error fetching daily stats:", error);

      return {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.message || "Failed to fetch daily stats",
        data: {},
      };
    }
  },
};

export const FastTagApi = {
  /**
   * Get FASTAG summary grouped by toll plaza
   * @param {object} params - { date, start_date, end_date, plaza, driver_id }
   */
  getFastagSummary: async (params = {}) => {
    try {
      const response = await apiClient.post("fastag/summary", params);

      return {
        success: true,
        data: response.data?.data || [],
        totals: response.data?.totals || {},
        message: response.data?.message || "FASTAG summary fetched successfully",
      };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to fetch FASTAG summary",
        data: [],
        totals: {},
      };
    }
  },
  /**
   * Get FASTAG drivers passed at a specific toll plaza
   * @param {object} params - { plaza_name, date, start_date, end_date, driver_id }
   */
  getFastagPlazaDetails: async (params = {}) => {
    try {
      const response = await apiClient.post("fastag/plaza-details", params);

      return {
        success: true,
        data: response.data?.data || {},
        message: response.data?.message || "FASTAG plaza details fetched successfully",
      };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message || "Failed to fetch FASTAG plaza details",
        data: {},
      };
    }
  },
};

export const DriversSalary = {
  /**
   * Get driver attendance & salary details
   * @param {string} client_id
   * @param {object} params
   */
  getDriverAttendanceRecordForDB: async (client_id, params = {}) => {
    try {
      console.log("🚀 Fetching driver attendance with params:", {
        client_id,
        ...params,
      });

      const response = await apiClient.post(
        `getDriverAttendanceRecordforDB/${client_id}`,
        params
      );

      console.log("✅ Driver attendance response:", response.data);

      return {
        success: true,
        data: response.data?.data || [],
        summary: response.data?.summary || {},
        message:
          response.data?.message ||
          "Driver attendance fetched successfully",
      };
    } catch (error) {
      console.error("❌ Error fetching driver attendance:", error);

      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message ||
          "Failed to fetch driver attendance",
        data: [],
        summary: {},
      };
    }
  },

  /**
   * Get Driver Salary Transactions
   * @param {object} params - Query parameters for filtering and pagination
   */
  getDriverSalary: async (params = {}) => {
    try {
      console.log("🚀 Fetching driver salary transactions:", params);

      const response = await apiClient.get(`/getDriverSalary`, { params });

      console.log("✅ Driver salary transactions response:", response.data);

      return {
        success: true,
        data: response.data?.data || [],
        pagination: response.data?.pagination || {},
        message: response.data?.message || "Driver salary transactions fetched successfully",
      };
    } catch (error) {
      console.error("❌ Error fetching driver salary transactions:", error);

      return {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.message || "Failed to fetch driver salary transactions",
        data: [],
        pagination: {},
      };
    }
  },

  /**
   * Update Driver Salary Transaction
   * @param {number} id - Transaction ID
   * @param {object} payload - Update data
   */
  updateDriverSalary: async (id, payload = {}) => {
    try {
      console.log("🚀 Updating driver salary transaction:", { id, payload });

      const response = await apiClient.put(`/driver-salary-status/${id}`, payload);

      console.log("✅ Driver salary update response:", response.data);

      return {
        success: true,
        message: response.data?.message || "Driver salary transaction updated successfully",
      };
    } catch (error) {
      console.error("❌ Error updating driver salary transaction:", error);

      return {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.message || "Failed to update driver salary transaction",
      };
    }
  },

  /**
   * Update Driver Deposit (Credit Note) Status
   * @param {number} id - Driver salary (payslip) ID
   * @param {object} payload - { deposit_status, deposit_remark }
   */
  updateDriverDeposit: async (id, payload = {}) => {
    try {
      const response = await apiClient.put(`/driver-deposit-status/${id}`, payload);
      return {
        success: true,
        message: response.data?.message || "Driver deposit updated successfully",
      };
    } catch (error) {
      console.error("Error updating driver deposit:", error);
      return {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.message || "Failed to update driver deposit",
      };
    }
  },

  /**
   * Create Driver Salary Invoice
   * @param {object} payload
   */
  createDriverSalaryInvoice: async (payload = {}) => {
    try {
      console.log("🚀 Creating driver salary invoice:", payload);

      const response = await apiClient.post(
        `/createDriverSalaryInvoice`,
        payload
      );

      console.log("✅ Salary invoice response:", response.data);

      return {
        success: true,
        invoiceId: response.data?.invoice_id,
        invoiceNumber: response.data?.invoice_number,
        message:
          response.data?.message ||
          "Driver salary invoice created successfully",
      };
    } catch (error) {
      console.error("❌ Error creating salary invoice:", error);

      return {
        success: false,
        status: error.response?.status,
        message:
          error.response?.data?.message ||
          "Failed to create driver salary invoice",
      };
    }
  },
};
  // Knowledge Articles (Categories) API
  export const knowledgeArticleApis = {
    getAll: async () => {
      try {
        const response = await apiClient.get("/getAllKnowledgeArticles");
        return response.data;
      } catch (error) {
        console.error("Error fetching knowledge articles:", error);
        throw error;
      }
    },

    add: async (formData) => {
      try {
        const response = await apiClient.post("/addKnowledgeArticles", formData);
        return response.data;
      } catch (error) {
        console.error("Error adding knowledge article:", error);
        throw error;
      }
    },

    updateById: async (id, formData) => {
      try {
        const response = await apiClient.post(`/updateKnowledgeArticle/${id}`, formData);
        return response.data;
      } catch (error) {
        console.error("Error updating knowledge article:", error);
        throw error;
      }
    },

    deleteById: async (id) => {
      try {
        const response = await apiClient.delete(`/deleteKnowledgeArticle/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting knowledge article:", error);
        throw error;
      }
    },
  };

  // Knowledge Base (Articles) API
  export const knowledgeBaseApis = {
    getAll: async (params = {}) => {
      try {
        const {
          page = 1,
          limit = 10,
          title = '',
          status = null,
          user_type = null,
          timestamps = null
        } = params;

        const queryParams = new URLSearchParams();
        queryParams.append('page', page);
        queryParams.append('limit', limit);

        if (title) queryParams.append('title', title);
        if (status !== null) queryParams.append('status', status);
        if (user_type) queryParams.append('user_type', user_type);
        if (timestamps) queryParams.append('timestamps', timestamps);

        const response = await apiClient.get(`/GetKnowledagebases?${queryParams.toString()}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching knowledge bases:", error);
        throw error;
      }
    },

    add: async (formData) => {
      try {
        const response = await apiClient.post("/AddKnowledagebase", formData);
        return response.data;
      } catch (error) {
        console.error("Error adding knowledge base:", error);
        throw error;
      }
    },

    getById: async (id) => {
      try {
        const response = await apiClient.post(`/GetKnowledagebaseById/${id}`, {});
        return response.data;
      } catch (error) {
        console.error("Error fetching knowledge base by id:", error);
        throw error;
      }
    },

    updateById: async (id, formData) => {
      try {
        const response = await apiClient.post(`/UpdateKnowledagebase/${id}`, formData);
        return response.data;
      } catch (error) {
        console.error("Error updating knowledge base:", error);
        throw error;
      }
    },

    deleteById: async (id) => {
      try {
        const response = await apiClient.delete(`/DeleteKnowledagebase/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting knowledge base:", error);
        throw error;
      }
    },
  };

export const userPermissionApis = {
  getOtherPermissions: async () => {
    try {
      const response = await apiClient.get("/getUserOtherPermissions");
      return response.data;
    } catch (error) {
      console.error("Error fetching user permissions:", error);
      throw error;
    }
  },
};

export const notificationApis = {
  getByUserId: async (userId) => {
    try {
      const response = await apiClient.post(`/getNotification/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },
  checkStatus: async (userId) => {
    try {
      const response = await apiClient.post("/checkNotificationStatus", {
        user_id: userId,
      });
      return response.data;
    } catch (error) {
      console.error("Error checking notification status:", error);
      throw error;
    }
  },
  markAllRead: async (userId) => {
    try {
      const response = await apiClient.post("/updateUserNotificationStatus", {
        user_id: userId,
      });
      return response.data;
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      throw error;
    }
  },
  add: async (data) => {
    try {
      const response = await apiClient.post("/addNotification", data);
      return response.data;
    } catch (error) {
      console.error("Error adding notification:", error);
      throw error;
    }
  },
};
